import { useEffect, useRef, useState, useLayoutEffect } from "react";
import { Button, ModalBody, ModalFooter } from "reactstrap";
import { api } from "../util/api";
import { DetailedErrorAlert } from "../components/DetailedErrorAlert";

export const ChatView = ({ caseId, userName }) => {
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  //load conversation
  useEffect(() => {
    let abort = false;

    (async () => {
      try {
        const res = await api.get(`/cases/${caseId}/chat_messages`);
        const messagesFromServer = res.data.map((m) => ({
          id: m.id,
          message_uuid: m.message_uuid,
          role: m.role,
          content: m.content,
          user_salutation: m.user_salutation,
          liked: m.liked,
          created_at: new Date(m.created_at).getTime(),
        }));
        if (!abort) setMessages(messagesFromServer);
      } catch (e) {
        console.error("Failed to load conversation.", e);
        if (!abort) setError(e);
        if (!abort) setMessages([]);
      }
      if (!abort) setLoading(false);
    })();

    return () => {
      abort = true;
    };
  }, [caseId]);

  //scroll to latest user message
  const scrollUserOnCreate = useRef(null);

  useLayoutEffect(() => {
    const id = scrollUserOnCreate.current;
    if (!id) return;
    const el = document.querySelector(`[data-uuid="${id}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    scrollUserOnCreate.current = null;
  }, [messages]);

  //send message to chatbot
  async function onSend() {
    if (sending) return;
    setSending(true);

    const content = input.trim();
    if (!content) {
      setSending(false);
      return;
    }

    setInput("");

    const userMsg = mkMsg("user", content, userName);
    scrollUserOnCreate.current = userMsg.message_uuid;

    const botMsg = mkMsg("assistant", "...", "");

    setMessages((prev) => [...prev, userMsg, botMsg]);

    try {
      await api.post(`/cases/${caseId}/chat_messages`, {
        userMsg: {
          message_uuid: userMsg.message_uuid,
          content: userMsg.content,
        },
        botMsg: {
          message_uuid: botMsg.message_uuid,
        },
      });

      const botMsgRsp = await pollForResponse(botMsg.message_uuid);

      setMessages((prev) =>
        prev.map((m) => (m.message_uuid === botMsg.message_uuid ? { ...m, id: botMsgRsp.id, content: botMsgRsp.content } : m))
      );
    } catch (e) {
      console.error("Failed to post message.", e);
      setError(e);
    }

    setSending(false);
  }

  //poll for chatbot response
  async function pollForResponse(message_uuid) {
    const maxAttempts = 30;
    let pollInterval = 500;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const res = await api.get(`/cases/${caseId}/chat_messages/${message_uuid}/`);
      if (res.status === 200) {
        return res.data;
      } else if (res.status === 202) {
        await new Promise((resolve) => setTimeout(resolve, pollInterval));
        pollInterval = Math.min(pollInterval * 2, 1000);
      }
    }

    throw new Error("Timeout");
  }

  //create initial user and bot message objects client side
  function mkMsg(role, content, user_salutation) {
    return {
      message_uuid: crypto.randomUUID(),
      role,
      content,
      user_salutation,
      created_at: Date.now(),
    };
  }

  //rate chatbot response
  async function onRate(id, liked) {
    if (!id) return;

    const prevMessage = messages.find((m) => m.id === id);
    if (!prevMessage) return;

    const nextLiked = prevMessage.liked === liked ? null : liked;

    setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, liked: nextLiked } : m)));

    try {
      const res = await api.patch(`/cases/${caseId}/chat_messages/${prevMessage.message_uuid}/`, {
        liked: nextLiked,
      });
    } catch (e) {
      console.error("Failed to patch rating.", e);
      setError(e);
      setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, liked: prevMessage.liked } : m)));
    }
  }

  return (
    <>
      <div className="modal-header">
        <h5 className="modal-title">
          <span className="chat_view__dot" aria-hidden />
          Chat with careviso AI Assistant
        </h5>

        <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
      </div>
      <ModalBody>
        <DetailedErrorAlert error={error} />
        <main className="chat_view__chat" aria-live="polite">
          {messages.length === 0 ? (
            <div className="chat_view__empty">
              {loading ? (
                <p>Loading...</p>
              ) : (
                <>
                  <p>Start a conversation to see responses here.</p>
                  <p>Tip: Shift+Enter for newline. Enter to send.</p>
                </>
              )}
            </div>
          ) : (
            messages.map((m) => <MessageRow key={m.message_uuid} msg={m} onRate={onRate} />)
          )}
        </main>
      </ModalBody>
      <ModalFooter>
        <div className="chat_view__prompt">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                onSend();
              }
            }}
            placeholder="Hello! How can I help?"
            className="chat_view__textarea"
          />
          <div className="chat_view__hint">
            AI can make mistakes, please <code>review</code> carefully.
          </div>
        </div>
        <Button outline color="primary" onClick={onSend} type="button">
          Send
        </Button>
        <Button outline color="secondary" data-bs-dismiss="modal" type="button">
          Close
        </Button>
      </ModalFooter>
    </>
  );
};

function MessageRow({ msg, onRate }) {
  const isUser = msg.role === "user";
  return (
    <div className="chat_view__msg" data-uuid={msg.message_uuid}>
      <div className={`chat_view__avatar ${isUser ? "chat_view__avatarUser" : "chat_view__avatarBot"}`}>{isUser ? "U" : "AI"}</div>
      <div className="chat_view__bubble">
        <p className="chat_view__messageContent">{msg.content}</p>
        <div className="chat_view__meta">
          <span>{isUser ? msg.user_salutation : "Assistant"}</span>
          <span>•</span>
          <time dateTime={new Date(msg.created_at).toISOString()}>
            {new Date(msg.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
          </time>
          {!isUser && (
            <div className="chat_view__ratingControls">
              <LikeBtn active={msg.liked === true} label="Like" onClick={() => onRate(msg.id, true)}>
                👍
              </LikeBtn>
              <LikeBtn active={msg.liked === false} label="Dislike" onClick={() => onRate(msg.id, false)}>
                👎
              </LikeBtn>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function LikeBtn({ active, label, onClick, children }) {
  return (
    <Button
      onClick={onClick}
      title={label}
      aria-label={label}
      className={`chat_view__likeBtn ${active ? "chat_view__likeBtnActive" : ""}`}
      type="button"
    >
      {children}
    </Button>
  );
}
